import { Transport, TransportHeaders, TransportRequest, TransportResponse } from "./Transport"
import https from "https"
import http from "http"
import { createBrotliDecompress, createGunzip, createInflate } from "zlib"
import { PassThrough, pipeline, Transform, Writable } from "stream"

type Protocol = typeof http | typeof https

export class HttpTransport implements Transport {
  static create(): HttpTransport {
    return new HttpTransport()
  }

  send(request: TransportRequest): Promise<TransportResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(request.url)
      const protocol = this.protocol(url)
      const options = this.options(request, url)

      const req: http.ClientRequest = protocol.request(options, (res) => {
        if (res.statusCode === undefined || res.statusCode <= 0) {
          reject(new Error(`Http status code: ${res.statusCode}`))
          return
        }

        const chunks: Uint8Array[] = []
        const readStream = this.getReadableStream(res)
        const writeStream = new Writable({
          write(chunk, _enc, cb) {
            chunks.push(chunk)
            cb()
          }
        })

        pipeline(readStream, writeStream, (err) => {
          if (err) {
            reject(new Error(`Failed to parse response: (${err.message})`))
          } else {
            if (!res.statusCode) return
            const body = Buffer.concat(chunks).toString("utf8")
            resolve(new TransportResponse(res.statusCode, body, this.responseHeadersFromIncomingMessage(res)))
          }
        })
      })

      req.on("timeout", () => {
        req.destroy()
        reject(new Error("Request timeout"))
      })
      req.on("error", (err) => reject(err))

      if (request.body) {
        req.write(request.body)
      }

      req.end()
    })
  }

  private getReadableStream(response: http.IncomingMessage): Transform {
    const contentEncodingType = response.headers["content-encoding"]

    switch (contentEncodingType) {
      case "br":
        return response.pipe(createBrotliDecompress())
      case "deflate":
        return response.pipe(createInflate())
      case "gzip":
        return response.pipe(createGunzip())
      default:
        return response.pipe(new PassThrough())
    }
  }

  private protocol(url: URL): Protocol {
    switch (url.protocol) {
      case "http:":
        return http
      case "https:":
        return https
      default:
        throw new Error(`Unsupported protocol: [${url.protocol}] (expected: http, https)`)
    }
  }

  private options(request: TransportRequest, url: URL): http.RequestOptions {
    return {
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname,
      port: url.port,
      method: request.method,
      headers: {
        [TransportHeaders.ACCEPT_ENCODING]: "gzip,deflate,br",
        ...request.headers.raw()
      },
      timeout: request.options.timeoutMillis ?? undefined
    }
  }

  private responseHeadersFromIncomingMessage(message: http.IncomingMessage): TransportHeaders {
    const builder = TransportHeaders.builder()
    for (const headerName in message.headers) {
      const headerValue = message.headers[headerName]
      switch (typeof headerValue) {
        case "string":
          builder.add(headerName, headerValue)
          break
        case "undefined":
          break
        default:
          if (headerValue.length > 0) {
            builder.add(headerName, headerValue[0])
          }
      }
    }
    return builder.build()
  }
}
